import { motion } from "framer-motion";
import { Target, Eye, Compass, ShieldCheck, Users, Lightbulb, HandHeart, Award } from "lucide-react";
import { Reveal, SectionHead } from "../components/Reveal";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity", desc: "We say what we'll do, then do what we said — every sprint, every invoice." },
  { icon: Lightbulb, title: "Innovation", desc: "We prototype fast, adopt what works, and never ship technology for its own sake." },
  { icon: Users, title: "Collaboration", desc: "Your team and ours operate as one — shared channels, shared goals, shared wins." },
  { icon: Award, title: "Excellence", desc: "Code reviews, QA gates and design polish are non-negotiable parts of our craft." },
  { icon: HandHeart, title: "Client First", desc: "We measure success by your outcomes, not our deliverables." },
  { icon: Compass, title: "Ownership", desc: "Every engineer at VAELIX owns the product like a founder would." },
];

const TIMELINE = [
  { year: "2021", title: "Founded in Pune", desc: "VAELIX TECHNOLOGY begins with three engineers and a conviction: Indian IT can be premium." },
  { year: "2022", title: "First Enterprise Client", desc: "Delivered a fintech payment platform processing its first million transactions." },
  { year: "2023", title: "AI Practice Launched", desc: "Built our AI & automation division, shipping LLM-powered products across industries." },
  { year: "2024", title: "50+ Clients Milestone", desc: "Crossed 50 happy clients and expanded into cloud-native and DevOps consulting." },
  { year: "2025", title: "100+ Projects Delivered", desc: "A century of shipped products — with a 99% client satisfaction record." },
  { year: "2026", title: "Going Global", desc: "Serving clients across three continents from our Pune innovation hub." },
];

export default function About() {
  return (
    <main className="pt-28">
      <section data-testid="about-story-section" className="section-pad !pt-8">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-block text-xs font-mono font-medium uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-5">About VAELIX</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]">
              Engineering excellence, <span className="text-gradient">made in Pune</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              VAELIX TECHNOLOGY was founded on a simple belief: businesses deserve software partners who care as much about outcomes as they do about code. From our hub in Pune, Maharashtra, we've grown into a 25+ strong team of engineers, designers and architects serving clients worldwide.
            </p>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              Five years, 100+ projects and a 99% satisfaction rate later, we remain obsessed with the same things — clean architecture, honest communication and products that move real business metrics.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <motion.div whileHover={{ scale: 1.015 }} transition={{ duration: 0.3 }} className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-indigo-500/20 to-cyan-400/20 rounded-3xl blur-2xl" aria-hidden="true" />
              <img src="https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="VAELIX TECHNOLOGY engineering team collaborating in Pune office"
                className="relative rounded-3xl border border-border/60 shadow-2xl w-full object-cover aspect-[4/3]" data-testid="about-team-image" />
            </motion.div>
          </Reveal>
        </div>
      </section>

      <section data-testid="mission-vision-section" className="section-pad bg-secondary/40 border-y border-border/60">
        <div className="container-x grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="glass-card rounded-2xl p-8 h-full">
              <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 text-white flex items-center justify-center"><Target size={22} /></span>
              <h2 className="mt-5 text-2xl font-bold tracking-tight">Our Mission</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">To empower businesses of every size with enterprise-grade technology that is honest in pricing, elegant in design and relentless in performance.</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="glass-card rounded-2xl p-8 h-full">
              <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 text-white flex items-center justify-center"><Eye size={22} /></span>
              <h2 className="mt-5 text-2xl font-bold tracking-tight">Our Vision</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">To be the most trusted technology partner emerging from India — a company the world's best businesses choose when failure is not an option.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="values-section" className="section-pad">
        <div className="container-x">
          <SectionHead badge="Our Values" title="What We Stand For" sub="Six principles that guide every line of code and every client conversation." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} data-testid={`value-card-${i}`}
                initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08 }} whileHover={{ y: -6 }}
                className="glass-card rounded-2xl p-6">
                <span className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center"><v.icon size={20} /></span>
                <h3 className="mt-4 font-semibold">{v.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-gallery-section" className="section-pad !pt-0">
        <div className="container-x">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80",
              "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=800&q=80",
              "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80",
              "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
            ].map((src, i) => (
              <motion.img key={src} src={src} alt="Life at VAELIX TECHNOLOGY"
                data-testid={`about-gallery-img-${i}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-border/60 shadow-lg w-full h-44 sm:h-56 object-cover hover:scale-[1.03] transition-transform duration-300" />
            ))}
          </div>
        </div>
      </section>

      <section data-testid="timeline-section" className="section-pad bg-secondary/40 border-y border-border/60">
        <div className="container-x max-w-3xl">
          <SectionHead badge="Our Journey" title="The VAELIX Timeline" />
          <div className="relative pl-8 border-l-2 border-primary/25 space-y-10">
            {TIMELINE.map((t, i) => (
              <motion.div key={t.year} data-testid={`timeline-item-${i}`}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }} className="relative">
                <span className="absolute -left-[41px] top-1 w-4 h-4 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-400 ring-4 ring-background" aria-hidden="true" />
                <span className="font-mono text-xs font-semibold text-primary tracking-widest">{t.year}</span>
                <h3 className="mt-1 font-display font-semibold text-lg">{t.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
